<template>
  <div
    aria-live="polite"
    role="document"
    tabindex="0"
    class="t-strip"
    :style="{left: tweenLeft + 'px'}"
    v-on:keydown="keydown"
  >
    <div
      v-for="page in nearbyPages"
      class="page-wrapper"
      :key="page.pageNum"
      :style="{
        left: pageWidth * page.stripOffset + 'px',
        width: viewerWidth + 'px',
      }"
      @touchstart="touchstart"
      @touchend="touchend"
    >
      <Page
        :offScreen="page.pageNum !== pageNum"
        :ref="`page${page.pageNum}`"
        :searchTerms="searchTerms"
        :viewerHeight="viewerHeight"
        :viewerWidth="viewerWidth"
        v-bind="page"
      />
    </div>
  </div>
</template>

<style scoped>
.t-strip {
  position: absolute;
  overflow: visible;
}

.page-wrapper {
  position: absolute;
  overflow: hidden;
}
</style>

<script>
import TweenLite from 'gsap/umd/TweenLite'
import config from '../config'
import Page from './Page.vue'

export default {
  name: 'Strip',

  inheritAttrs: false,

  props: {
    pages: {
      type: Array,
      required: true,
    },
    pageNum: {
      type: Number,
      required: true,
    },
    searchTerms: {
      type: String,
      required: true,
    },
    viewerHeight: {
      type: Number,
      required: true,
    },
    viewerWidth: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      pagePadding: 5,
      tweenLeft: 0,
    }
  },

  computed: {
    nearbyPages() {
      return this.pages
        .slice(
          Math.max(0, this.pageNum - config.keepBackward - 1),
          this.pageNum + config.keepForward - 1,
        )
        .filter(p => p.fetched)
    },

    pageWidth() {
      return this.viewerWidth + this.pagePadding
    },

    left() {
      return -(this.pageWidth * (this.pageNum - 1))
    },
  },

  created() {
    this.tweenLeft = this.left
  },

  watch: {
    viewerWidth() {
      this.tweenLeft = this.left
    },

    pageNum() {
      TweenLite.to(this, 0.3, {tweenLeft: this.left})
    },
  },

  methods: {
    getPage(pageNum) {
      const p = this.$refs[`page${pageNum}`]
      return p && p[0] // array because it's inside v-for
    },

    zoomIn() {
      const p = this.getPage(this.pageNum)
      p && p.zoomIn()
    },

    zoomOut() {
      const p = this.getPage(this.pageNum)
      p && p.zoomOut()
    },

    panTo(xy, pageNum = null) {
      const p = this.getPage(pageNum || this.pageNum)
      return p && p.panTo(xy)
    },

    setZoom(zoom, pageNum = null) {
      const p = this.getPage(pageNum || this.pageNum)
      p && p.setZoom(zoom)
    },

    keydown(event) {
      const {activeElement} = document
      if (activeElement) {
        if (activeElement.matches('input, select, textarea')) {
          return
        }
        if (event.key === ' ' && activeElement.matches('button')) {
          return
        }
      }

      const page = this.getPage(this.pageNum)
      switch (event.key) {
        case ' ': {
          const [dir, changePage] = event.shiftKey
            ? [-1, this.pagePrevious]
            : [1, this.pageNext]
          const panned = page.panTo({dy: 0.9 * dir})
          if (!panned) {
            changePage()
          }
          break
        }
        case 'ArrowUp':
          page.panTo({dy: -0.1})
          break
        case 'ArrowLeft':
          page.panTo({dx: -0.1})
          break
        case 'ArrowDown':
          page.panTo({dy: 0.1})
          break
        case 'ArrowRight':
          page.panTo({dx: 0.1})
          break
        case 'PageDown':
          this.pageNext()
          break
        case 'PageUp':
          this.pagePrevious()
          break
        case 'Home':
          this.panTo({y: 0})
          break
        case 'End':
          this.panTo({y: 1})
          break
      }
    },

    // Prevent default double-tap behavior of browser zooming.
    // We're handling that ourselves, and everything gets very
    // confusing when the controls zoom.
    touchstart(event) {
      event.preventDefault()
    },
    touchend(event) {
      event.preventDefault()
    },
  },

  components: {
    Page,
  },

  inject: ['pageNext', 'pagePrevious'],
}
</script>
